/**
 * Registry of "flat-form" target markets — countries whose regulatory fields
 * live in the MAIN customer-account form and persist via the base/jurisdiction
 * split (US, Canada). This is the ONE place that knows which form keys belong to
 * which market's registry entry: `company-data.ts` (split/merge) and the wizard's
 * persist+hydrate loop both read it (the key lists themselves live beside the
 * schemas, in FLAT_FORM_MARKET_FIELDS), so adding a flat-form country is one entry here + its market
 * schema in `customer-account.ts` (and its module in the Target Markets hub).
 *
 * EU is intentionally NOT here: it self-persists from its own module
 * (EuJurisdictionModule) because its shape (27 member states) doesn't fit the
 * flat schema. Data-driven spec-only countries (UK, the rest of the EU-27) are
 * visualization in GenericJurisdictionModule until they get a real schema.
 */
import { FLAT_FORM_MARKET_FIELDS } from '@/schemas/customer-account';

export interface FlatFormMarket {
  /** ISO-2 market code (matches company_jurisdictions.market_code). */
  code: string;
  /** Form keys carved into company_jurisdictions[code].data (everything else is
   *  the company base). Derived from the Zod schemas so it never drifts. */
  keys: ReadonlySet<string>;
}

// Keys come from FLAT_FORM_MARKET_FIELDS (beside the schemas), which the form
// validator also uses to check only the markets a company has added.
export const FLAT_FORM_MARKETS: readonly FlatFormMarket[] = (['US', 'CA', 'AU'] as const).map(
  (code) => ({ code, keys: new Set<string>(FLAT_FORM_MARKET_FIELDS[code]) }),
);

export const FLAT_FORM_MARKET_CODES: readonly string[] = FLAT_FORM_MARKETS.map((m) => m.code);

/** True if a market code persists via the main-form split (vs EU self-persist
 *  or a visualization-only spec module). */
export function isFlatFormMarket(code: string): boolean {
  return FLAT_FORM_MARKETS.some((m) => m.code === code);
}

/** A target market the wizard can offer (every one has a registration module). */
export interface JurisdictionDef {
  code: string;
  /** Full label incl. regulator — shown in the add dropdown. */
  label: string;
  /** Short label — shown on the (space-constrained) tab. */
  short: string;
}

export const JURISDICTIONS: readonly JurisdictionDef[] = [
  { code: 'US', label: 'United States (FDA)', short: 'United States' },
  // The EU is ONE region market (MDR): an EU-wide block + a member-state picker
  // that reveals thin per-country cards — not 27 separate markets.
  { code: 'EU', label: 'European Union (EMA)', short: 'European Union' },
  { code: 'CA', label: 'Canada (Health Canada)', short: 'Canada' },
  { code: 'UK', label: 'United Kingdom (MHRA)', short: 'United Kingdom' },
  { code: 'AU', label: 'Australia (TGA)', short: 'Australia' },
];

/** The short name shown on a market's tab ("Canada"), or the code if unknown. */
export function marketShortName(code: string): string {
  return JURISDICTIONS.find((j) => j.code === code)?.short ?? code;
}
